import * as THREE from 'three';
import type { WorldMap } from './protocol';
import type { GraphicsSettings } from './graphics-settings';

/** A bounded, texel-snapped shadow volume follows the current view. */
export class SunShadows {
  readonly light = new THREE.DirectionalLight(0xffedce, 3);
  private direction = new THREE.Vector3(7, 15, 8).normalize();
  private right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), this.direction).normalize();
  private up = new THREE.Vector3().crossVectors(this.direction, this.right).normalize();
  private focus = new THREE.Vector3();
  private forward = new THREE.Vector3();
  private radius = 18;
  private mapSize = 0;
  constructor(scene: THREE.Scene) {
    this.light.shadow.bias = -.0001; this.light.shadow.normalBias = .015;
    scene.add(this.light, this.light.target);
  }
  setLighting(direction: THREE.Vector3, color: string, intensity: number): void {
    this.direction.copy(direction).normalize();
    this.right.crossVectors(new THREE.Vector3(0,1,0),this.direction);
    if (this.right.lengthSq() < 1e-8) this.right.set(1,0,0); else this.right.normalize();
    this.up.crossVectors(this.direction,this.right).normalize();
    this.light.color.set(color); this.light.intensity = intensity;
  }
  configure(settings: GraphicsSettings, size: number): void {
    if (this.light.intensity === 0) size = 0;
    this.light.castShadow = size > 0 && this.light.intensity > 0;
    this.radius = Math.min(settings.viewDistance / 2, settings.shadows === 'high' ? 24 : settings.shadows === 'medium' ? 18 : 12);
    if (this.mapSize !== size) {
      this.light.shadow.map?.dispose(); this.light.shadow.map = null;
      this.light.shadow.mapSize.set(Math.max(1, size), Math.max(1, size)); this.mapSize = size;
    }
    Object.assign(this.light.shadow.camera, { left: -this.radius, right: this.radius, top: this.radius, bottom: -this.radius });
    this.light.shadow.camera.updateProjectionMatrix();
  }
  update(camera: THREE.PerspectiveCamera, map: WorldMap | null): void {
    camera.getWorldDirection(this.forward); this.forward.y = 0;
    this.focus.copy(camera.position).addScaledVector(this.forward, this.radius * .5);
    if (map) {
      this.focus.x = THREE.MathUtils.clamp(this.focus.x, map.bounds.min.x, map.bounds.max.x);
      this.focus.z = THREE.MathUtils.clamp(this.focus.z, map.bounds.min.z, map.bounds.max.z);
    }
    this.focus.y = map ? (map.bounds.min.y + map.bounds.max.y) / 2 : 3;
    const texel = 2 * this.radius / Math.max(1, this.mapSize);
    for (const axis of [this.right, this.up]) { const value = this.focus.dot(axis); this.focus.addScaledVector(axis, Math.round(value / texel) * texel - value); }
    const distance = this.radius * 3 + (map ? map.bounds.max.y - map.bounds.min.y : 10);
    this.light.position.copy(this.focus).addScaledVector(this.direction, distance); this.light.target.position.copy(this.focus);
    this.light.target.updateMatrixWorld();
    if (this.light.shadow.camera.far !== distance * 2) { this.light.shadow.camera.far = distance * 2; this.light.shadow.camera.updateProjectionMatrix(); }
  }
  get diagnostics() { return { mapSize: this.mapSize, halfExtent: this.radius, center: this.focus.toArray() }; }
  dispose(): void { this.light.shadow.dispose(); }
}
